/* GENERATED FILE — DO NOT EDIT.
 *
 * The check-in vocabulary and the weigh-in's water tags, emitted from the enums the phone compiles:
 *   HybridTrainer/Models/CheckInField.swift  (`CheckInField`, `WaterTag`, `CheckInVocabulary`)
 *
 * Regenerate: KATA_EMIT_CHECK_IN_FIELDS=1 ./tools/engine-tests/run.sh
 * Gate `n2-the-weekly-check-in` regenerates this in memory on every run and goes red on any
 * difference, so a hand edit here cannot survive.
 *
 * ⚠️ **`coach_workouts.check_in_fields` AND `body_measurements.water_tags` HAVE NO CHECK
 * CONSTRAINT**, so the database will accept any string. That is exactly why the web must send the
 * phone's own raw values: a `bodyfat` written from a second vocabulary would store cleanly, draw
 * nothing on the athlete's sheet, and say nothing about why.
 *
 * ⚠️ **WHAT IS NOT SELECTABLE IS NOT IN THIS LIST AT ALL** — the enum's own rule. The date, the
 * notes and the three photos are always present on a check-in, so they are not offered as choices
 * anywhere. The water tag is not a field either: it qualifies the weight wherever the weight is asked.
 */

export type CheckInField = {
  readonly key: string;
  readonly label: string;
  readonly group: string;
  readonly groupTitle: string;
  readonly columns: readonly string[];
  readonly scaleEnds?: readonly [string, string];
};

export const CHECK_IN_FIELDS: readonly CheckInField[] = [
  { key: "weight", label: "Body weight", group: "composition", groupTitle: "Body composition", columns: ["weight_kg"] },
  { key: "bodyFat", label: "Body fat %", group: "composition", groupTitle: "Body composition", columns: ["body_fat_percent"] },
  { key: "neck", label: "Neck", group: "torso", groupTitle: "Neck & torso", columns: ["neck_cm"] },
  { key: "chest", label: "Chest", group: "torso", groupTitle: "Neck & torso", columns: ["chest_cm"] },
  { key: "shoulders", label: "Shoulders", group: "torso", groupTitle: "Neck & torso", columns: ["shoulders_cm"] },
  { key: "waist", label: "Waist", group: "torso", groupTitle: "Neck & torso", columns: ["waist_cm"] },
  { key: "hips", label: "Hips", group: "torso", groupTitle: "Neck & torso", columns: ["hips_cm"] },
  { key: "upperArm", label: "Upper arm", group: "arms", groupTitle: "Arms", columns: ["left_upper_arm_cm", "right_upper_arm_cm"] },
  { key: "forearm", label: "Forearm", group: "arms", groupTitle: "Arms", columns: ["left_forearm_cm", "right_forearm_cm"] },
  { key: "thigh", label: "Thigh", group: "legs", groupTitle: "Legs", columns: ["left_thigh_cm", "right_thigh_cm"] },
  { key: "calf", label: "Calf", group: "legs", groupTitle: "Legs", columns: ["left_calf_cm", "right_calf_cm"] },
  { key: "sleepHours", label: "Sleep", group: "week", groupTitle: "This week", columns: ["sleep_hours"] },
  { key: "appetite", label: "Appetite", group: "week", groupTitle: "This week", columns: ["appetite"], scaleEnds: ["Much less", "Much more"] },
  { key: "trainingDesire", label: "Desire to train", group: "week", groupTitle: "This week", columns: ["training_desire"], scaleEnds: ["None", "Eager"] },
  { key: "lifeEvent", label: "Life event", group: "week", groupTitle: "This week", columns: ["life_event", "life_event_note"] },
];

export const CHECK_IN_FIELD_COUNT = 15;

export type WaterTag = { readonly key: string; readonly label: string };

export const WATER_TAGS: readonly WaterTag[] = [
  { key: "creatineStart", label: "Creatine start" },
  { key: "refeedDeload", label: "Refeed / deload" },
  { key: "phaseChange", label: "Phase change" },
  { key: "saltyDay", label: "Salty day" },
  { key: "travel", label: "Travel" },
  { key: "illness", label: "Illness" },
];

/** The groups in the sheet's own order, each with its fields.
 *  ⚠️ Derived from the list rather than restated, so a field added to the enum cannot land in a
 *  group nobody draws. */
export function checkInGroups(): { group: string; title: string; fields: CheckInField[] }[] {
  const out: { group: string; title: string; fields: CheckInField[] }[] = [];
  for (const f of CHECK_IN_FIELDS) {
    const last = out[out.length - 1];
    if (last !== undefined && last.group === f.group) last.fields.push(f);
    else out.push({ group: f.group, title: f.groupTitle, fields: [f] });
  }
  return out;
}

/** Stored in the VOCABULARY'S order, never the order the coach happened to tap.
 *  The athlete's sheet draws them in sheet order, and two orders would be two answers. */
export function inVocabularyOrder(chosen: readonly string[]): string[] {
  return CHECK_IN_FIELDS.filter((f) => chosen.includes(f.key)).map((f) => f.key);
}

/** A stored tag list as labels, in the vocabulary's order; an unknown tag is dropped, never shown raw. */
export function waterTagLabels(stored: readonly string[] | null | undefined): string[] {
  const have = stored ?? [];
  return WATER_TAGS.filter((t) => have.includes(t.key)).map((t) => t.label);
}
